"use client"

import { getAuthHeader } from "../utils/auth"; // ★ 1. 認証ヘッダー取得関数をインポート
import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useOutletContext, useNavigate } from "react-router-dom"
import { JAPAN_GTM_ASSISTANT_URL, isEnglishOperatorMode } from "../utils/operatorMode";

// --- SVG Icons ---
const SaveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2 2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>;
const DuplicateIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>;
const PreviewIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>;

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

function TemplatePreview({ template, sampleTarget, onPreviewUpdate }) {
    const { user } = useOutletContext();
    const selectedClientId = useMemo(() => (user?.role === "admin" ? localStorage.getItem("selectedClientId") || "" : user?.client_id), [user]);

    useEffect(() => {
        if (!template || !sampleTarget || !selectedClientId) {
            onPreviewUpdate({ subject: "", body: "" });
            return;
        }

        onPreviewUpdate({ subject: "プレビューを生成中...", body: "..." });

        // --- ▼▼▼ 修正点 1/1 ▼▼▼ ---
        // プレビューAPIに list_type: 'master' を追加
        // (このページのサンプルは常に 'master' リストから取得するため)
        const payload = {
            template_id: template.id,
            sales_list_id: sampleTarget.id,
            client_id: selectedClientId,
            list_type: 'master' // ★ 修正: 'master' を明示的に指定
        };
        // --- ▲▲▲ 修正ここまで ▲▲▲ ---

        fetch(`${API_URL}/api/templates/preview`, {
            method: 'POST',
            headers: { // ★ 2. 認証ヘッダーを追加
                'Content-Type': 'application/json',
                ...getAuthHeader()
            },
            body: JSON.stringify(payload)
        })
        .then(res => res.json())
        .then(data => {
            if (data.success) {
                onPreviewUpdate({ subject: data.subject, body: data.body });
            } else {
                onPreviewUpdate({ subject: "プレビューの生成に失敗", body: data.error || "サーバーエラー" });
            }
        })
        .catch(err => {
            onPreviewUpdate({ subject: "プレビューの生成に失敗", body: `通信エラー: ${err.message}` });
        });

    }, [template, sampleTarget, selectedClientId, onPreviewUpdate, user]);

    return null;
}


function Templates({ settingsPath = '/settings' }) {
    const { user } = useOutletContext();
    const navigate = useNavigate();
    const isEnglishBeta = isEnglishOperatorMode();
    const [templates, setTemplates] = useState([]);
    // ▼▼▼ 修正点: `allow_resend` をstateの初期値から削除 ▼▼▼
    const [editingTemplate, setEditingTemplate] = useState({ title: "", subject: "", body: "", company_profile_id: "" });
    const [editId, setEditId] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [companyProfiles, setCompanyProfiles] = useState([]);
    const [selectedPreviewId, setSelectedPreviewId] = useState(null);
    const [previewContent, setPreviewContent] = useState({ subject: "", body: "" });
    const [samplePreviewTarget, setSamplePreviewTarget] = useState(null);

    const selectedClientId = useMemo(() => (user?.role === "admin" ? localStorage.getItem("selectedClientId") || "" : user?.client_id), [user]);
  
    const fetchAllData = useCallback(() => {
        if (!selectedClientId) return;

        fetch(`${API_URL}/api/templates?client_id=${selectedClientId}`, { headers: getAuthHeader() }) // ★ 2. 認証ヘッダーを追加
            .then(res => res.json())
            .then(data => { if (data.success) setTemplates((data.templates || []).sort((a,b) => b.id - a.id)); });
        
        fetch(`${API_URL}/api/company-profiles?client_id=${selectedClientId}&role=${user.role}&email=${user.email}`, { headers: getAuthHeader() }) // ★ 2. 認証ヘッダーを追加
            .then(res => res.json())
            .then(data => { if (data.success) setCompanyProfiles(data.profiles || []); });
        
        // ★ 修正: サンプル取得時も listSourceType: 'master' を明示的に指定
        fetch(`${API_URL}/api/load`, {
            method: 'POST',
            headers: { // ★ 2. 認証ヘッダーを追加
                'Content-Type': 'application/json',
                ...getAuthHeader()
            },
            body: JSON.stringify({ 
                client_id: selectedClientId, 
                page: 1, 
                limit: 1,
                listSourceType: 'master' // ★ 修正: 'master' を明示的に指定
            })
        }).then(res => res.json()).then(data => {
            if (data.success && data.data.length > 0) {
                setSamplePreviewTarget(data.data[0]);
            }
        });
    }, [selectedClientId, user.role, user.email]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        // ▼▼▼ 修正点: checkbox用の分岐を削除し、シンプルに `value` のみで更新 ▼▼▼
        setEditingTemplate(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (companyProfiles.length === 0) {
            if (window.confirm(isEnglishBeta
                ? "No company profile is available for outreach execution yet.\nOpen Company Profile & Suppression first?"
                : "送信に使用する「会社概要テンプレート」がまだ作成されていません。\n先に設定画面に移動して、テンプレートを作成しますか？")) {
                navigate(settingsPath);
            }
            return;
        }

        if (!editingTemplate.title || !editingTemplate.subject || !editingTemplate.body || !editingTemplate.company_profile_id) {
            return alert(isEnglishBeta
                ? "Template name, company profile, Japanese subject, and Japanese body are required."
                : "テンプレート名、使用する会社概要、件名、本文はすべて必須項目です。");
        }
        const url = editId ? `${API_URL}/api/templates/${editId}` : `${API_URL}/api/templates`;
        const method = editId ? "PUT" : "POST";
    
        // ▼▼▼ 修正点: payloadから `allow_resend` を削除 ▼▼▼
        const payload = { ...editingTemplate, client_id: selectedClientId };

        try {
            const res = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", ...getAuthHeader() }, // ★ 2. 認証ヘッダーを追加
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error(isEnglishBeta ? "Could not save the template." : "保存に失敗しました。");
            alert(isEnglishBeta ? `Template ${editId ? 'updated' : 'saved'}.` : `テンプレートを${editId ? '更新' : '保存'}しました。`);
            resetForm();
            fetchAllData();
        } catch (err) {
            alert(err.message);
        }
    };
  
    const handleDelete = async (id) => {
        if (!window.confirm(isEnglishBeta ? "Delete this template?" : "このテンプレートを削除しますか？")) return;
        try {
            const res = await fetch(`${API_URL}/api/templates/${id}`, {
                method: "DELETE",
                headers: getAuthHeader() // ★ 2. 認証ヘッダーを追加
            });
            if(!res.ok) throw new Error(isEnglishBeta ? "Could not delete the template." : "削除に失敗しました。");
            fetchAllData();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleEdit = (template) => {
        setEditId(template.id);
        // ▼▼▼ 修正点: `allow_resend` をセットする処理を削除 ▼▼▼
        setEditingTemplate({ ...template });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
  
    const handleDuplicate = (template) => {
        resetForm();
        setEditingTemplate({
            ...template,
            id: null,
            title: `${template.title} (コピー)`,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
  
    const resetForm = () => {
        setEditId(null);
        // ▼▼▼ 修正点: `allow_resend` をリセット対象から削除 ▼▼▼
        setEditingTemplate({ title: "", subject: "", body: "", company_profile_id: "" });
    };

    const filteredTemplates = useMemo(() => {
        return templates.filter(t => 
            Object.values(t).some(val => String(val).toLowerCase().includes(searchText.toLowerCase()))
        );
    }, [templates, searchText]);
  
    const FormLabel = ({ children }) => <label className="text-sm font-medium text-gray-600 block mb-1">{children}<span className="text-red-500 ml-1">*</span></label>;
    
    const allowedTags = ["{{会社名}}"];

    return (
        <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
            <div className="max-w-7xl mx-auto space-y-8">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{isEnglishBeta ? "Templates" : "🧩 文面テンプレート管理"}</h1>
                        {isEnglishBeta && (
                            <p className="mt-2 text-sm text-gray-600 max-w-3xl">
                                Create and review Japanese outreach copy. The operator shell is English, but subject lines and message bodies should remain Japanese for Japan-facing execution.
                            </p>
                        )}
                    </div>
                    {isEnglishBeta && (
                        <a
                            href={JAPAN_GTM_ASSISTANT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex w-full items-center justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 sm:w-auto"
                        >
                            Open Japan GTM Assistant
                        </a>
                    )}
                </div>

                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold text-gray-700 mb-4 border-b pb-2">{isEnglishBeta ? (editId ? 'Edit template' : 'Create new template') : (editId ? 'テンプレート編集' : '新規テンプレート作成')}</h2>
                    {isEnglishBeta && (
                        <div className="mb-4 rounded-lg border border-cyan-100 bg-cyan-50 p-4 text-sm text-slate-700">
                            Use the assistant to check Japanese tone, form etiquette, and first-contact phrasing. SalesBreaker will not execute anything from the assistant.
                        </div>
                    )}
                    <form onSubmit={handleSave} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <FormLabel>{isEnglishBeta ? "Template name" : "テンプレート名"}</FormLabel>
                                <input type="text" name="title" required value={editingTemplate.title} onChange={handleFormChange} className="w-full p-2 border rounded-md" maxLength="100" />
                            </div>
                            <div>
                                <FormLabel>{isEnglishBeta ? "Company profile for context" : "使用する会社概要"}</FormLabel>
                                <select name="company_profile_id" required value={editingTemplate.company_profile_id} onChange={handleFormChange} className="w-full p-2 border rounded-md bg-white">
                                    {companyProfiles.length === 0 ? (
                                        <option value="" disabled>{isEnglishBeta ? "Create a company profile in Company Profile & Suppression first" : "先に[各種設定]から会社概要テンプレートを作成してください"}</option>
                                    ) : (
                                        <>
                                            <option value="">{isEnglishBeta ? "Select company profile" : "選択してください"}</option>
                                            {companyProfiles.map(p => (<option key={p.id} value={p.id}>{p.profile_name}</option>))}
                                        </>
                                    )}
                                </select>
                            </div>
                        </div>
                        <div><FormLabel>{isEnglishBeta ? "Japanese subject" : "件名"}</FormLabel><input type="text" name="subject" required value={editingTemplate.subject} onChange={handleFormChange} className="w-full p-2 border rounded-md" maxLength="200"/></div>
                        <div><FormLabel>{isEnglishBeta ? "Japanese body" : "本文"}</FormLabel><textarea name="body" required value={editingTemplate.body} onChange={handleFormChange} className="w-full p-2 border rounded-md" rows="6"></textarea></div>
                        {/* ▼▼▼ 修正点: 「再送を許可する」のチェックボックスがあった行をまるごと削除 ▼▼▼ */}
                        <div className="flex items-center justify-end">
                            <div className="grid w-full grid-cols-1 gap-2 sm:w-auto sm:grid-cols-2">
                                {editId && <button type="button" onClick={resetForm} className="bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-md hover:bg-gray-300">{isEnglishBeta ? "Cancel" : "キャンセル"}</button>}
                                <button type="submit" className="flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700"><SaveIcon/> {isEnglishBeta ? (editId ? 'Update template' : 'Save template') : (editId ? '更新する' : '保存する')}</button>
                            </div>
                        </div>
                    </form>
                </div>
        
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold text-gray-700 mb-4 border-b pb-2">{isEnglishBeta ? "Available variables" : "差し込み可能タグ一覧"}</h2>
                    <p className="text-sm text-gray-600 mb-4">{isEnglishBeta ? "Click a variable to copy it. Values are inserted from your company profile and shared variables." : "タグをクリックするとクリップボードにコピーされます。会社概要テンプレートで設定した値が差し込まれます。"}</p>
                    <div className="flex flex-wrap gap-2">
                        {allowedTags.map(tag => (<button type="button" key={tag} onClick={() => navigator.clipboard.writeText(tag)} className="bg-gray-200 text-gray-800 text-xs font-mono py-1 px-3 rounded-full hover:bg-gray-300">{tag}</button>))}
                    </div>
                </div>
        
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold text-gray-700 mb-4">{isEnglishBeta ? "Saved templates" : "📚 保存済みテンプレート一覧"}</h2>
                    <input type="text" placeholder={isEnglishBeta ? "Search templates..." : "テンプレートを検索..."} value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full p-2 border rounded-md mb-4"/>
                    <div className="space-y-4">
                        {filteredTemplates.map(t => {
                            const linkedProfile = companyProfiles.find(p => p.id === t.company_profile_id);
                            return (
                                <div key={t.id} className="border p-4 rounded-lg">
                                    <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                                        <div className="min-w-0">
                                            <h3 className="font-bold text-gray-800">{t.title}</h3>
                                            <p className="text-sm text-gray-500 break-words">{isEnglishBeta ? "Subject" : "件名"}: {t.subject}</p>
                                            <p className="text-sm text-gray-500">{isEnglishBeta ? "Company profile" : "使用する会社概要"}: {linkedProfile?.profile_name || (isEnglishBeta ? 'Not set' : '未設定')}</p>
                                        </div>
                                        <div className="grid grid-cols-4 gap-2 flex-shrink-0 sm:flex sm:items-center">
                                            <button onClick={() => setSelectedPreviewId(selectedPreviewId === t.id ? null : t.id)} className="p-2 text-gray-600 hover:bg-gray-200 rounded-full" title="プレビュー"><PreviewIcon/></button>
                                            <button onClick={() => handleEdit(t)} className="p-2 text-gray-600 hover:bg-gray-200 rounded-full" title="編集"><EditIcon/></button>
                                            <button onClick={() => handleDuplicate(t)} className="p-2 text-gray-600 hover:bg-gray-200 rounded-full" title="複製"><DuplicateIcon/></button>
                                            <button onClick={() => handleDelete(t.id)} className="p-2 text-red-500 hover:bg-red-100 rounded-full" title="削除"><DeleteIcon/></button>
                                        </div>
                                    </div>
                                    {selectedPreviewId === t.id && (
                                        <>
                                            <TemplatePreview template={t} sampleTarget={samplePreviewTarget} onPreviewUpdate={setPreviewContent} />
                                            <div className="mt-4 p-4 bg-gray-100 rounded-md space-y-4">
                                                <div>
                                                    <h4 className="text-sm font-semibold text-gray-600 mb-1">{isEnglishBeta ? "Preview target" : "プレビュー"} ({isEnglishBeta ? "target" : "対象"}: {samplePreviewTarget?.company || (isEnglishBeta ? 'Sample company' : 'サンプル企業')})</h4>
                                                    <p className="p-2 bg-white rounded text-sm break-words">{previewContent.subject}</p>
                                                </div>
                                                <div>
                                                    <h4 className="text-sm font-semibold text-gray-600 mb-1">{isEnglishBeta ? "Body preview:" : "本文プレビュー:"}</h4>
                                                    <pre className="p-2 bg-white rounded text-sm whitespace-pre-wrap font-sans break-words">{previewContent.body}</pre>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Templates;
